/* eslint-disable check-file/filename-blocklist */
import type { ComponentType, FC } from 'react';

import type { SSR_PARTIAL_COMPONENT } from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess';

import type { LoadingPriority } from './constants';
import type { MustPreloadLoadableNames } from './MustPreloadLoadableNames';

declare global {
	interface Window {
		__SSR_RENDERED__?: boolean;
		__LOADABLE__?: string[];
		__CC_REACT_DOM_CONTEXT_PATCH__?: boolean;
		__ENABLE_REACT_CONTEXT_PATCH__?: boolean;
		__HYDRATION_ACTIVATED_IDS__?: Set<string>;
		__HYDRATION_FAILED__?: boolean;
		__HYDRATION_UNLOCKED_IDS__?: Set<string>;
		__HYDRATION_UNLOCKED_HOSTS__?: WeakSet<HTMLElement>;
		__REACT_ROOT__?: {
			unstable_scheduleHydration?: (target: Element | undefined) => void;
		};
	}
}

export type SSRSuccessCheckName = keyof typeof SSR_PARTIAL_COMPONENT;

export type LoadedModuleType<P> = ModuleType<P> | { default: ModuleType<P> };

export type LoaderResultType<P> = Promise<LoadedModuleType<P>>;

export type LoadableExportType = <P extends {}>(options: DefinitionType<P>) => LoadableType<P>;

export type Task<T> = () => PromiseLike<T> | T;

export enum ActivationTriggerType {
	HOVER = 'hover',
	IMMEDIATE = 'immediate',
	SCROLL_INTO_VIEW = 'scroll_into_view',
}

export enum ActivationReason {
	AUTO_TIMEOUT = 'auto-timeout',
	USER_ACTIVATION = 'user-activation',
	IMMEDIATE_UNLOCK = 'immediate-unlock',
	FALLBACK = 'fallback',
}

export type ActivationTrigger = { type: ActivationTriggerType };

export type FutureTask<T> = {
	run(): void;
	result: Promise<T>;
	readonly started: boolean;
};

export type QueueItem<T> = {
	id: string;
	futureTask: FutureTask<T>;
	priority: number;
	_delayHydration?: boolean;
};

export type PriorityTask<T> = {
	id: string;
	task: Task<T>;
	priority: number;
	_delayHydration?: boolean;
};

export type TaskManagerOptions = {
	maxQueueTimeMs?: number;
	debounceTimeMs?: number;
	disabled?: boolean;
	totalHydratedAtOnce?: number;
};

export type ModuleType<P> = ComponentType<P> | null | undefined;

export type ReactLoadableCompatibleLoadingProps = {
	error?: any;
	retry?: () => void;
};

export type createLoadableComponentProps<P extends {}> = {
	displayName: string;
	loadableName: string;
	priority: LoadingPriority;
	load: (priorityOverride?: number, isHydrated?: boolean) => Promise<ModuleType<P> | Error>;
	loadedModuleRef: { ref: ModuleType<P> };
};

export type DefinitionType<P> = {
	loader: () => LoaderResultType<P>;
	// Loading placeholder
	loading?: ComponentType<P & ReactLoadableCompatibleLoadingProps>;
	// name is only for dev purpose. Use id if you need to uniquely identify a component.
	name?: string;
	// Make Loadable compatible with react-loadable
	isReactLoadable?: boolean;
	//This helps override react-loadable priority to any other value other than AFTER_PAINT
	overrideReactLoadablePriority?: number;
	// React loadable render equivalent https://github.com/jamiebuilds/react-loadable#customizing-rendering
	render?: (loaded: ModuleType<P>, args: P) => any;
	/**
	 * Option to delay loader function based on network conditions, as some browsers will cache failed dynamic imports which can break loadable components
	 * @default false
	 * @see https://go/j/AFB-192
	 * @see https://go/j/ED-27993
	 * @see https://github.com/whatwg/html/issues/6768
	 */
	waitForNetworkOnline?: boolean | (() => boolean);

	// Optional array of activation triggers. When this is present, the loadable will NOT hydrate on the client until the user activates it
	triggers?: ActivationTrigger[];
	// Value used to denote whether or not the Loadable rendered correctly during SSR. This should only be used for LoadableActivatable
	ssrSuccessCheckName?: SSRSuccessCheckName;
	// internal id added by LoadableBabelPlugin
	__loadable_id__?: string;
	// A list of loadable names that must be loaded when rendering SPA over SSR
	_mustPreloadWhenRenderingSPA?: MustPreloadLoadableNames | MustPreloadLoadableNames[];
	// Because we render a subtree of SPA in SSR. We need to mark where is the root.
	// We only consider ancestors up to this component when determining the id for the server placeholder.
	_reactTreeRootForServerPlaceholderId?: boolean;
	// Used to determine if hydration should be delayed for this component over SSR output
	_delayHydration?: boolean;
	// used for priority hydrate on hover to hydrate immidiatly instead of waiting for hover
	hydrateImmediately?: () => boolean | undefined;
	// Add a wrapper around the component to allow adding data-vc-placeholder and data-vc-placeholder-replace
	_addVCPlaceholderWrapper?: string;
	// Ignore reporting children of this component in TTVC
	_vcPlaceholderIgnored?: boolean;
	// Loader function can return a sync data instead of a promise
	_maybeSyncLoader?: boolean;
};

export type LoadableType<P = {}> = {
	preload: () => void | LoaderResultType<P>;
	hydrateOnHover: () => void | LoaderResultType<P>;
	setLoadedModule: (module: ModuleType<P> | Error) => void;
} & FC<P & { children?: any }> &
	ComponentType<P & { children?: any }>;
